import React, { useState } from "react";
import toast from "react-hot-toast";
import { convertCodingLanguage } from "../services/jobService";
import "./QuestionBankManager.css";

// {value, label} — value is what's stored as reference_language (mirrors
// company_question_bank.py's SUPPORTED_LANGUAGES); label is what recruiters
// actually see, so "cpp"/"csharp" don't show up as raw, unformatted text.
export const LANGUAGES = [
  { value: "python", label: "Python" },
  { value: "java", label: "Java" },
  { value: "javascript", label: "JavaScript" },
  { value: "c", label: "C" },
  { value: "cpp", label: "C++" },
  { value: "csharp", label: "C#" },
  { value: "ruby", label: "Ruby" },
  { value: "swift", label: "Swift" },
];

/* ── Download template (CSV — no client-side xlsx library needed) ─────────── */

export function downloadTemplate(isMcq) {
  const rows = isMcq
    ? [
        ["Question", "Option A", "Option B", "Option C", "Option D", "Correct Answer"],
        ["What is the capital of France?", "Berlin", "Madrid", "Paris", "Rome", "Paris"]
      ]
    : [
        ["Question", "Reference Solution", "Language", "Test Case Input", "Expected Output", "Test Case Group"],
        ["Print the sum of two integers given on one line", "print(sum(map(int, input().split())))", "python", "2 3", "5", "sum_two_ints"],
        ["", "", "", "10 20", "30", "sum_two_ints"]
      ];
  const csv = rows
    .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
    .join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = isMcq ? "mcq_question_template.csv" : "coding_question_template.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* ── MCQ question form (add + edit) ────────────────────────────────────── */

export function McqQuestionForm({ initial, onSave, onCancel }) {
  const [question, setQuestion] = useState(initial?.question || "");
  const [options, setOptions] = useState([
    initial?.option_a || "", initial?.option_b || "", initial?.option_c || "", initial?.option_d || ""
  ]);
  const [correctIdx, setCorrectIdx] = useState(() => {
    if (!initial) return 0;
    const idx = [initial.option_a, initial.option_b, initial.option_c, initial.option_d]
      .findIndex((o) => o === initial.correct_answer);
    return idx >= 0 ? idx : 0;
  });
  const [saving, setSaving] = useState(false);

  const updateOption = (i, val) => setOptions((prev) => prev.map((o, idx) => (idx === i ? val : o)));

  const handleSubmit = async () => {
    if (!question.trim() || options.some((o) => !o.trim())) {
      toast.error("Question and all four options are required.");
      return;
    }
    setSaving(true);
    try {
      await onSave({
        question: question.trim(),
        option_a: options[0].trim(),
        option_b: options[1].trim(),
        option_c: options[2].trim(),
        option_d: options[3].trim(),
        correct_answer: options[correctIdx].trim()
      });
    } catch (err) {
      toast.error(err.message || "Unable to save question");
    } finally {
      setSaving(false);
    }
  };

  // A plain div, not a <form> — this component is also rendered inside
  // CreateJobModal.jsx's own outer <form>, and a <form> can't validly nest
  // inside another <form>. When it did, the browser silently misrouted this
  // "Save question" button's click to the OUTER form's submit handler
  // instead of this one, so editing a question inside Create Job appeared
  // to do nothing. Using a plain button + onClick sidesteps native form
  // submission entirely, so this works the same whether nested in another
  // form (Create Job) or not (Manage Questions / pipeline).
  return (
    <div className="qbm-form">
      <label>
        <span>Question</span>
        <textarea value={question} onChange={(e) => setQuestion(e.target.value)} rows={2} placeholder="Question text" />
      </label>
      <div className="qbm-options-grid">
        {["A", "B", "C", "D"].map((letter, i) => (
          <label key={letter} className="qbm-option-row">
            <input
              type="radio"
              name="correct-option"
              checked={correctIdx === i}
              onChange={() => setCorrectIdx(i)}
              title="Mark as correct answer"
            />
            <span className="qbm-option-letter">{letter}</span>
            <input
              type="text"
              value={options[i]}
              onChange={(e) => updateOption(i, e.target.value)}
              placeholder={`Option ${letter}`}
            />
          </label>
        ))}
      </div>
      <div className="qbm-form-actions">
        {onCancel && <button type="button" className="secondary-button" onClick={onCancel}>Cancel</button>}
        <button type="button" className="primary-button" disabled={saving} onClick={handleSubmit}>{saving ? "Saving…" : "Save question"}</button>
      </div>
    </div>
  );
}

/* ── Coding question form (add + edit) ─────────────────────────────────── */

export function CodingQuestionForm({ jobId, initial, onSave, onCancel }) {
  const [question, setQuestion] = useState(initial?.question || "");
  const [solution, setSolution] = useState(initial?.reference_solution || "");
  const [language, setLanguage] = useState(initial?.reference_language || "python");
  const [testCases, setTestCases] = useState(
    initial?.test_cases?.length ? initial.test_cases.map((t) => ({ input: t.input || "", expected_output: t.expected_output || "" }))
      : [{ input: "", expected_output: "" }]
  );
  const [saving, setSaving] = useState(false);
  const [previewLang, setPreviewLang] = useState(
    (LANGUAGES.find((l) => l.value !== (initial?.reference_language || "python")) || LANGUAGES[1]).value
  );
  const [previewCode, setPreviewCode] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const handlePreview = async () => {
    // This needs an already-saved question on an already-created job — it
    // calls the backend's per-question /convert endpoint, which only exists
    // once the question has a real question_id. That's normally guaranteed
    // by only showing this section when initial?.question_id is set (below);
    // jobId missing here would be a caller wiring bug, not a real scenario —
    // surfaced instead of silently doing nothing so it's never mysterious.
    if (!jobId || !initial?.question_id) {
      toast.error("Save this question first before previewing it in another language.");
      return;
    }
    setPreviewing(true);
    setPreviewCode(null);
    try {
      const result = await convertCodingLanguage(jobId, initial.question_id, previewLang);
      setPreviewCode(result.code);
    } catch (err) {
      toast.error(err.message || "Unable to generate preview");
    } finally {
      setPreviewing(false);
    }
  };

  const updateCase = (i, field, val) =>
    setTestCases((prev) => prev.map((tc, idx) => (idx === i ? { ...tc, [field]: val } : tc)));

  const addCase = () => setTestCases((prev) => [...prev, { input: "", expected_output: "" }]);
  const removeCase = (i) => setTestCases((prev) => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    const cleanedCases = testCases.filter((tc) => tc.expected_output.trim());
    if (!question.trim() || !solution.trim() || cleanedCases.length === 0) {
      toast.error("Question, reference solution, and at least one test case (with expected output) are required.");
      return;
    }
    setSaving(true);
    try {
      await onSave({
        question: question.trim(),
        reference_solution: solution,
        reference_language: language,
        test_cases: cleanedCases
      });
    } catch (err) {
      toast.error(err.message || "Unable to save question");
    } finally {
      setSaving(false);
    }
  };

  // Plain div, not <form> — see the matching comment in McqQuestionForm for
  // why (nested <form>s break inside CreateJobModal.jsx's outer form).
  return (
    <div className="qbm-form">
      <label>
        <span>Question</span>
        <textarea value={question} onChange={(e) => setQuestion(e.target.value)} rows={2} placeholder="Problem statement" />
      </label>
      <label>
        <span>Reference solution language</span>
        <select value={language} onChange={(e) => setLanguage(e.target.value)}>
          {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
        </select>
      </label>
      <label>
        <span>Reference solution</span>
        <textarea
          className="qbm-code"
          value={solution}
          onChange={(e) => setSolution(e.target.value)}
          rows={6}
          placeholder="Reference solution code"
        />
      </label>
      {initial?.question_id && (
        <div className="qbm-preview-lang">
          <span className="qbm-testcases-label">Preview reference solution in another language</span>
          <div className="qbm-preview-lang-row">
            <select value={previewLang} onChange={(e) => { setPreviewLang(e.target.value); setPreviewCode(null); }}>
              {LANGUAGES.filter((l) => l.value !== language).map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
            <button type="button" className="secondary-button" onClick={handlePreview} disabled={previewing}>
              {previewing ? "Generating…" : "Preview"}
            </button>
          </div>
          {previewCode && <pre className="qbm-code-preview">{previewCode}</pre>}
        </div>
      )}
      <div className="qbm-testcases">
        <span className="qbm-testcases-label">Test cases</span>
        {testCases.map((tc, i) => (
          <div key={i} className="qbm-testcase-row">
            <textarea
              className="qbm-code qbm-testcase-input"
              rows={2}
              placeholder={"Input — one line per stdin read, e.g.\n3 5\n1 2 3 4 5"}
              value={tc.input}
              onChange={(e) => updateCase(i, "input", e.target.value)}
            />
            <textarea
              className="qbm-code qbm-testcase-input"
              rows={2}
              placeholder="Expected output"
              value={tc.expected_output}
              onChange={(e) => updateCase(i, "expected_output", e.target.value)}
            />
            {testCases.length > 1 && (
              <button type="button" className="icon-button qbm-remove-case" onClick={() => removeCase(i)}>×</button>
            )}
          </div>
        ))}
        <button type="button" className="link-button" onClick={addCase}>+ Add test case</button>
      </div>
      <div className="qbm-form-actions">
        {onCancel && <button type="button" className="secondary-button" onClick={onCancel}>Cancel</button>}
        <button type="button" className="primary-button" disabled={saving} onClick={handleSubmit}>{saving ? "Saving…" : "Save question"}</button>
      </div>
    </div>
  );
}

/* ── One row in the question list ──────────────────────────────────────── */

export function QuestionRow({ question, isMcq, onEdit, onDelete }) {
  return (
    <div className="qbm-row">
      <div className="qbm-row-main">
        <p className="qbm-row-question">{question.question}</p>
        {isMcq ? (
          <div className="qbm-row-options">
            {["option_a", "option_b", "option_c", "option_d"].map((key) => (
              <span key={key} className={`qbm-row-opt${question[key] === question.correct_answer ? " correct" : ""}`}>
                {question[key]}
              </span>
            ))}
          </div>
        ) : (
          <div className="qbm-row-meta">
            <span className="qbm-lang-badge">{question.reference_language}</span>
            <span>{(question.test_cases || []).length} test case{(question.test_cases || []).length === 1 ? "" : "s"}</span>
            {question.verified === true && <span className="qbm-verify-badge qbm-verify-badge--ok">Verified</span>}
            {question.verified === false && (
              <span className="qbm-verify-badge qbm-verify-badge--warn" title={question.verification_note}>Unverified — review</span>
            )}
          </div>
        )}
      </div>
      <div className="qbm-row-actions">
        <button className="icon-button" type="button" title="Edit" onClick={onEdit}>✎</button>
        <button className="icon-button" type="button" title="Delete" onClick={onDelete}>🗑</button>
      </div>
    </div>
  );
}

/* ── Upload preview (Excel/CSV parse result, before confirming) ───────────── */

export function UploadPreview({ preview, isMcq, saving, onConfirm, onCancel }) {
  return (
    <div className="qbm-preview">
      <p className="qbm-preview-summary">
        {preview.valid_count} question{preview.valid_count === 1 ? "" : "s"} ready to import
        {preview.error_count > 0 && `, ${preview.error_count} row${preview.error_count === 1 ? "" : "s"} skipped`}
      </p>
      {preview.errors.length > 0 && (
        <ul className="qbm-preview-errors">
          {preview.errors.map((e, i) => (
            <li key={i}>Row {e.row}: {e.message}</li>
          ))}
        </ul>
      )}
      {preview.parsed.length > 0 && (
        <ul className="qbm-preview-list">
          {preview.parsed.map((q, i) => (
            <li key={i}>{q.question}</li>
          ))}
        </ul>
      )}
      <div className="qbm-form-actions">
        <button type="button" className="secondary-button" onClick={onCancel}>Cancel</button>
        <button
          type="button" className="primary-button"
          disabled={saving || preview.parsed.length === 0}
          onClick={onConfirm}
        >
          {saving ? "Saving…" : `Import ${preview.valid_count} question${preview.valid_count === 1 ? "" : "s"}`}
        </button>
      </div>
    </div>
  );
}
